import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render errors below it and displays a recoverable fallback card.
 * Without this, a single chart crash blanks the entire app shell. We
 * intentionally don't try to ship the error off-host — the CO is the only
 * user, and they can simply reload.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfacing render errors in the browser console is intentional — the
    // operator's only debug surface is DevTools.
    console.error('[render error]', error, info.componentStack);
  }

  private handleReset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="m-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <h2 className="font-semibold">Something went wrong rendering this page.</h2>
          <p className="mt-1 text-xs text-red-700">{this.state.error.message}</p>
          <button
            type="button"
            className="btn-secondary mt-3"
            onClick={() => {
              this.handleReset();
              window.location.reload();
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
